import { Layout } from 'antd';
import {
  DashboardOutlined,
  MessageOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@starling/core/auth/store';
import { useLogout, useContextsQuery } from '@/auth/hooks';
import { useListingsEnabled } from '@/features/hooks';
import { BlockedBanner } from '@/components/BlockedBanner';
import { AppSidebar } from '@/components/AppSidebar';
import { useTenantSettingsQuery } from '@/settings/hooks';

const { Sider, Content } = Layout;

// Desktop-раскладка кабинета оптики. Тёмный сайдбар (--deep-trust-blue) слева,
// контент справа. Иерархия сайдбара — единый паттерн AppSidebar:
// бренд → название оптики → навигация → карточка пользователя.
// Верхнего хедера нет: выход живёт внизу сайдбара, а контент (и баннер
// блокировки) начинается с самого верха.
export function AppLayout(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const user = useAuthStore((s) => s.user);
  // Название оптики для акцента сайдбара. Хук уже смонтирован BlockedBanner —
  // берём из общего кэша, лишнего запроса нет.
  const { data: tenant } = useTenantSettingsQuery();
  // Контексты аккаунта — питают переключатель оптики: ссылка «Сменить оптику»
  // видна только при 2+ активных membership. Query кэшируется (staleTime 5 мин).
  const { data: contextsData } = useContextsQuery();
  // Тумблер раздела «Объявления» — настройки платформы (доводка V1 №19).
  const listingsEnabled = useListingsEnabled();
  const canSwitchContext = (contextsData?.contexts.length ?? 0) >= 2;

  // Подсветка пункта сайдбара по префиксу пути — работает и на детальных
  // страницах (/customers/:id, /orders/:id).
  const path = location.pathname;
  let selectedKey = '/';
  if (path.startsWith('/customers')) selectedKey = '/customers';
  else if (path.startsWith('/orders')) selectedKey = '/orders';
  else if (path.startsWith('/messages')) selectedKey = '/messages';
  else if (path.startsWith('/listings')) selectedKey = '/listings';
  else if (path.startsWith('/settings')) selectedKey = '/settings';

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  // Карточка пользователя: если бэкенд отдал имя (claim `name`) — оно первой
  // строкой, роль второй; иначе роль первой строкой (предусловие №2 брифа).
  const roleLabel = user?.role === 'admin' ? t('users.role.admin') : t('users.role.employee');
  const userPrimary = user?.name ?? roleLabel;
  const userSecondary = user?.name ? roleLabel : undefined;
  const contextName = tenant?.name ?? t('app.title');

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="md"
        collapsedWidth={0}
        width={220}
        style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'auto' }}
      >
        <AppSidebar
          brandSubtitle={t('app.subtitle')}
          contextName={contextName}
          canSwitchContext={canSwitchContext}
          switchContextLabel={t('app.switch_context')}
          onSwitchContext={() => navigate('/switch-context')}
          selectedKey={selectedKey}
          mainSection={{
            label: t('nav.section_clients'),
            items: [
              { key: '/', to: '/', label: t('nav.dashboard'), icon: <DashboardOutlined /> },
              {
                key: '/customers',
                to: '/customers',
                label: t('nav.customers'),
                icon: <TeamOutlined />,
              },
              {
                key: '/orders',
                to: '/orders',
                label: t('nav.orders'),
                icon: <UnorderedListOutlined />,
              },
              // «Объявления» (V1.5) — контент, как заказы и сообщения, а не
              // настройка оптики: в основной секции сразу под заказами
              // (замечание приёмки 2026-09-07). Показ пункта — по тумблеру
              // платформы («Настройки» → «Приложения», доводка №19).
              ...(listingsEnabled
                ? [
                    {
                      key: '/listings',
                      to: '/listings',
                      label: t('nav.listings'),
                      icon: <ShopOutlined />,
                    },
                  ]
                : []),
              {
                key: '/messages',
                to: '/messages',
                label: t('nav.messages'),
                icon: <MessageOutlined />,
              },
            ],
          }}
          tailSection={{
            label: t('nav.section_optic'),
            items: [
              {
                key: '/settings',
                to: '/settings',
                label: t('nav.settings'),
                icon: <SettingOutlined />,
              },
            ],
          }}
          userPrimary={userPrimary}
          userSecondary={userSecondary}
          logoutLabel={t('app.logout')}
          onLogout={handleLogout}
        />
      </Sider>
      <Layout>
        <Content style={{ display: 'flex', flexDirection: 'column' }}>
          <BlockedBanner />
          <div style={{ padding: '24px 32px 40px', flex: 1 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
